import {
  GridColumnVisibilityModel,
  GridLogicOperator,
  type GridFilterItem,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-premium'
import moment from 'moment-timezone'
import { type LeadTableDateFilterValue } from './filters/LeadTableDateFilter'
import { loadPageSize } from './leadTablePreferences'
import { replaceMatchingFilterItem } from './filters/leadTableFilterModel'
import { convertToAbsoluteTimeRange } from 'components/DatePickerPopover/dateRangeUtils'


const STATUS_FILTER_FIELD = 'status'
const STATUS_FILTER_OPERATOR = 'isAnyOf'

export type DateFilterField = 'createdAt' | 'statusChangedAt'

type BackendPrimitiveType = 'string' | 'number' | 'boolean' | 'datetime'

type BackendColumnConfig = {
  columnName: string
  sortName?: string
  primitiveType: BackendPrimitiveType
}

type BackendBaseFilter = {
  column: string
  operator: string
  value: unknown[] | null
}

type BackendCompositeFilter = {
  and?: BackendFilter[]
  or?: BackendFilter[]
}

export type BackendFilter = BackendBaseFilter | BackendCompositeFilter

export type BackendSortDirection = 'asc' | 'desc'
export type BackendSort = Record<string, BackendSortDirection>

export function dashedToUnderscored(value: string) {
  return value.replace(/-/g, '_')
}

export function underscoredToDashed(value: string) {
  return value.replaceAll('_', '-')
}

function isCustomAttributeField(field: string) {
  return field.startsWith('clientCampaignCustomAttributes:')
}

function getCustomAttributeName(field: string): string {
  return field.replace(/^clientCampaignCustomAttributes:/, '')
}

const CUSTOM_FIELD_PREFIX = 'customFields:'

export const NUMERIC_CUSTOM_FIELD_TYPES = ['custom_bigint', 'custom_float']

export type LeadCustomFieldTypes = Record<string, string>

export function isCustomField(field: string) {
  return field.startsWith(CUSTOM_FIELD_PREFIX)
}

export function getCustomFieldName(field: string): string {
  return field.slice(CUSTOM_FIELD_PREFIX.length)
}

function getCustomFieldColumnConfig(
  field: string,
  customFieldTypes: LeadCustomFieldTypes
): BackendColumnConfig {
  const fieldType = customFieldTypes[getCustomFieldName(field)]

  return {
    columnName: `leads.custom-fields:${getCustomFieldName(field)}`,
    primitiveType: NUMERIC_CUSTOM_FIELD_TYPES.includes(fieldType) ? 'number' : 'string',
  }
}

function getBackendColumnConfig(
  field: string,
  customFieldTypes: LeadCustomFieldTypes = {}
): BackendColumnConfig {
  const fieldMap: Record<string, BackendColumnConfig> = {
    id: { columnName: 'leads.id', primitiveType: 'string' },
    createdAt: { columnName: 'leads.created-at', primitiveType: 'datetime' },
    clientId: { columnName: 'clients.id', sortName: 'clients.name', primitiveType: 'number' },
    clientCampaignId: {
      columnName: 'client-campaigns.id',
      sortName: 'client-campaigns.name',
      primitiveType: 'number',
    },
    campaignPublicId: { columnName: 'client-campaigns.public-id', primitiveType: 'string' },
    degreeProgramId: {
      columnName: 'degree-programs.id',
      sortName: 'degree-programs.name',
      primitiveType: 'number',
    },
    subId: { columnName: 'leads.subid', primitiveType: 'string' },
    source: { columnName: 'leads.source', primitiveType: 'string' },
    firstName: { columnName: 'leads.first-name', primitiveType: 'string' },
    lastName: { columnName: 'leads.last-name', primitiveType: 'string' },
    email: { columnName: 'leads.email', primitiveType: 'string' },
    phoneNumber: { columnName: 'leads.phone-number', primitiveType: 'string' },
    streetAddress: { columnName: 'leads.street-address', primitiveType: 'string' },
    city: { columnName: 'leads.city', primitiveType: 'string' },
    state: { columnName: 'leads.state', primitiveType: 'string' },
    zipCode: { columnName: 'leads.zip-code', primitiveType: 'string' },
    status: { columnName: 'leads.status', primitiveType: 'string' },
    statusCode: { columnName: 'leads.status-code', primitiveType: 'string' },
    disposition: { columnName: 'leads.disposition', primitiveType: 'string' },
    billable: { columnName: 'leads.billable', primitiveType: 'boolean' },
    rejectionReason: { columnName: 'leads.rejection-reason', primitiveType: 'string' },
    filteredReason: { columnName: 'leads.filtered-reason', primitiveType: 'string' },
    publicId: { columnName: 'leads.public-id', primitiveType: 'string' },
    test: { columnName: 'leads.test', primitiveType: 'boolean' },
    statusChangedAt: { columnName: 'leads.status-changed-at', primitiveType: 'datetime' },
    vendorId: { columnName: 'vendors.id', sortName: 'vendors.name', primitiveType: 'number' },
    externalId: { columnName: 'leads.external-id', primitiveType: 'string' },
    costInCents: { columnName: 'leads.cost-in-cents', primitiveType: 'number' },
    ipAddress: { columnName: 'leads.remote-ip', primitiveType: 'string' },
    userAgent: { columnName: 'leads.user-agent', primitiveType: 'string' },
    secondaryTcpa: { columnName: 'leads.secondary-tcpa', primitiveType: 'boolean' },
    rawRequestParameters: { columnName: 'leads.raw-params', primitiveType: 'string' },
    trackingParameters: { columnName: 'leads.tracking-params', primitiveType: 'string' },
    landingPageId: { columnName: 'landing-pages.public-id', primitiveType: 'string' },
    jornayaLeadidToken: { columnName: 'leads.jornaya-leadid-token', primitiveType: 'string' },
    complyedCreativeId: { columnName: 'leads.complyed-creative-id', primitiveType: 'number' },
  }

  if (isCustomAttributeField(field)) {
    return {
      columnName: `client-campaigns.custom-attributes:${getCustomAttributeName(field)}`,
      primitiveType: 'string',
    }
  }

  if (isCustomField(field)) {
    return getCustomFieldColumnConfig(field, customFieldTypes)
  }

  return fieldMap[field] ?? { columnName: `leads.${field}`, primitiveType: 'string' }
}

export function buildInitialPaginationModel(): GridPaginationModel {
  return {
    page: 0,
    pageSize: loadPageSize(),
  }
}

export function buildInitialSortModel(): GridSortModel {
  return [{ field: 'createdAt', sort: 'desc' }]
}

export function buildInitialFilterModel(timezone: string): GridFilterModel {
  const today = moment.tz(timezone)

  return {
    items: [
      {
        field: 'createdAt',
        operator: 'between',
        value: {
          operator: 'between',
          value: {
            label: 'Today',
            timeRange: [
              today.clone().startOf('day').format(),
              today.clone().endOf('day').format(),
            ],
          },
        },
      },
    ],
    logicOperator: GridLogicOperator.And,
  }
}

export function buildInitialColumnVisibilityModel(
  clientCampaignCustomAttributes: string[] = [],
  leadCustomFieldNames: string[] = []
): GridColumnVisibilityModel {
  return {
    campaignPublicId: false,
    costInCents: false,
    filteredReason: false,
    externalId: false,
    test: false,
    phoneNumber: false,
    streetAddress: false,
    city: false,
    state: false,
    zipCode: false,
    statusCode: false,
    disposition: false,
    statusChangedAt: false,
    ipAddress: false,
    userAgent: false,
    secondaryTcpa: false,
    rejectionReason: false,
    rawRequestParameters: false,
    trackingParameters: false,
    landingPageId: false,

    ...Object.fromEntries(
      clientCampaignCustomAttributes.map(attr => [
        `clientCampaignCustomAttributes:${attr}`,
        false,
      ])
    ),

    ...Object.fromEntries(
      leadCustomFieldNames.map(name => [
        `${CUSTOM_FIELD_PREFIX}${name}`,
        false,
      ])
    ),
  }
}

export function getIncludedStatusFilterValues(
  filterModel: GridFilterModel,
): string[] {
  return filterModel.items
    .filter(
      (item) =>
        item.field === STATUS_FILTER_FIELD &&
        item.operator === STATUS_FILTER_OPERATOR &&
        Array.isArray(item.value),
    )
    .flatMap((item) => item.value.map(String))
}

export function hasIncludedStatusFilter(
  filterModel: GridFilterModel,
): boolean {
  return filterModel.items.some(
    (item) =>
      item.field === STATUS_FILTER_FIELD &&
      item.operator === STATUS_FILTER_OPERATOR,
  )
}

export function toggleStatusInFilterModel(
  filterModel: GridFilterModel,
  status: string,
): GridFilterModel {
  const currentValues = getIncludedStatusFilterValues(filterModel)
  const nextValues = currentValues.includes(status)
    ? currentValues.filter((value) => value !== status)
    : [...currentValues, status]

  return replaceMatchingFilterItem(
    filterModel,
    (item) =>
      item.field === STATUS_FILTER_FIELD &&
      item.operator === STATUS_FILTER_OPERATOR,
    nextValues.length > 0
      ? {
          id: STATUS_FILTER_FIELD,
          field: STATUS_FILTER_FIELD,
          operator: STATUS_FILTER_OPERATOR,
          value: nextValues,
        }
      : null,
  )
}

function serializeBooleanValueForBackend(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase()

    if (normalizedValue === 'yes' || normalizedValue === 'true') {
      return true
    }

    if (normalizedValue === 'no' || normalizedValue === 'false') {
      return false
    }
  }

  return Boolean(value)
}

function serializeNumberValueForBackend(value: unknown): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value)

    if (!Number.isNaN(parsedValue)) {
      return parsedValue
    }
  }

  return null
}

function serializeStringValueForBackend(value: unknown): string {
  return String(value)
}

function normalizeSingleValueForPrimitiveType(
  value: unknown,
  primitiveType: BackendPrimitiveType
): string | number | boolean | null {
  if (primitiveType === 'boolean') {
    return serializeBooleanValueForBackend(value)
  }

  if (primitiveType === 'number') {
    return serializeNumberValueForBackend(value)
  }

  return serializeStringValueForBackend(value)
}

function normalizeArrayValuesForPrimitiveType(
  values: unknown[],
  primitiveType: BackendPrimitiveType
): Array<string | number | boolean> {
  if (primitiveType === 'boolean') {
    return values.map(serializeBooleanValueForBackend)
  }

  if (primitiveType === 'number') {
    return values
      .map(serializeNumberValueForBackend)
      .filter((value): value is number => value != null)
  }

  return values.map(serializeStringValueForBackend)
}

function isDateFilterValue(value: unknown): value is LeadTableDateFilterValue {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'operator' in value &&
      'value' in value &&
      value.operator === 'between'
  )
}

function dateFilterToBackendValue(
  value: LeadTableDateFilterValue['value'],
  timezone: string,
): string[] | null {
  if (value.label) {
    const absoluteTimeRange = convertToAbsoluteTimeRange({ label: value.label }, timezone)

    if (!absoluteTimeRange) return null

    return [
      absoluteTimeRange[0].format(),
      absoluteTimeRange[1].format(),
    ]
  }

  return value.timeRange
}

function mapMuiOperatorToBackendOperator(params: {
  operator: GridFilterItem['operator']
  primitiveType: BackendPrimitiveType
}): string | null {
  const { operator, primitiveType } = params

  switch (operator) {
    case 'contains':
      return 'contains'
    case 'doesNotContain':
      return 'not-contains'
    case 'startsWith':
      return 'starts-with'
    case 'endsWith':
      return 'ends-with'
    case 'isEmpty':
      return 'blank'
    case 'isNotEmpty':
      return 'not-blank'
    case 'isAnyOf':
      return 'list'
    case 'isNotAnyOf':
      return 'not-list'
    case 'is':
      return primitiveType === 'boolean' ? 'equals' : 'is'
    case 'not':
      return primitiveType === 'boolean' ? 'not-equal' : 'not'
    case '=':
    case '==':
    case 'equals':
      return primitiveType === 'number' || primitiveType === 'boolean' ? 'equals' : 'is'
    case 'doesNotEqual':
    case '!=':
      return primitiveType === 'number' || primitiveType === 'boolean' ? 'not-equal' : 'not'
    case 'after':
    case '>':
      return 'greater-than'
    case 'onOrAfter':
    case '>=':
      return 'greater-than-or-equal'
    case 'before':
    case '<':
      return 'less-than'
    case 'onOrBefore':
    case '<=':
      return 'less-than-or-equal'
    default:
      return operator ?? null
  }
}

const ID_BASED_LOOKUP_OPERATORS = ['list', 'not-list']

function shouldUseSortNameForFilter(operator: string) {
  return !ID_BASED_LOOKUP_OPERATORS.includes(operator)
}

function buildBaseFilter(params: {
  field: string
  operator: string
  value: unknown[] | null
  customFieldTypes?: LeadCustomFieldTypes
}): BackendBaseFilter | null {
  const { field, operator, value, customFieldTypes } = params
  const { columnName, sortName } = getBackendColumnConfig(field, customFieldTypes)

  if (
    operator !== 'blank' &&
    operator !== 'not-blank' &&
    (!value || value.length === 0)
  ) {
    return null
  }

  const backendColumn =
    sortName && shouldUseSortNameForFilter(operator)
      ? sortName
      : columnName

  return {
    column: backendColumn,
    operator,
    value,
  }
}

function stripVegaPrefix(value: unknown) {
  return typeof value === 'string' ? value.replace(/^VEGA-/i, '') : value
}

function getPrimitiveTypeForFilter(params: {
  columnConfig: BackendColumnConfig
  operator: string
}): BackendPrimitiveType {
  const { columnConfig, operator } = params

  return columnConfig.sortName && shouldUseSortNameForFilter(operator)
    ? 'string'
    : columnConfig.primitiveType
}

function buildEmptyValueFilter(params: {
  field: string
  operator: string
  customFieldTypes?: LeadCustomFieldTypes
}): BackendBaseFilter | null {
  return buildBaseFilter({
    field: params.field,
    operator: params.operator,
    value: [],
    customFieldTypes: params.customFieldTypes,
  })
}

function normalizeCostInCentsValue(value: unknown): unknown {
  return typeof value === 'number'
    ? Math.round(value * 100)
    : value
}

function gridFilterItemToBackendFilter(
  item: GridFilterItem,
  timezone: string,
  customFieldTypes: LeadCustomFieldTypes = {},
): BackendBaseFilter | null {
  if (!item.field || item.field === 'rowMenu') {
    return null
  }

  if (item.field === 'publicId') {
    item = { ...item, value: stripVegaPrefix(item.value) }
  }

  if (isDateFilterValue(item.value)) {
    return buildBaseFilter({
      field: item.field,
      operator: item.value.operator,
      value: dateFilterToBackendValue(item.value.value, timezone),
      customFieldTypes,
    })
  }

  if (
    item.value == null &&
    item.operator !== 'isEmpty' &&
    item.operator !== 'isNotEmpty'
  ) {
    return null
  }

  const columnConfig = getBackendColumnConfig(item.field, customFieldTypes)

  const operator = mapMuiOperatorToBackendOperator({
    operator: item.operator,
    primitiveType: columnConfig.primitiveType,
  })

  if (!operator) {
    return null
  }

  if (operator === 'blank' || operator === 'not-blank') {
    return buildEmptyValueFilter({
      field: item.field,
      operator,
      customFieldTypes,
    })
  }

  const primitiveType = getPrimitiveTypeForFilter({
    columnConfig,
    operator,
  })

  if (ID_BASED_LOOKUP_OPERATORS.includes(operator)) {
    const rawValues = Array.isArray(item.value) ? item.value : [item.value]

    const values =
      item.field === 'costInCents'
        ? rawValues.map(normalizeCostInCentsValue)
        : rawValues

    return buildBaseFilter({
      field: item.field,
      operator,
      value: normalizeArrayValuesForPrimitiveType(values, primitiveType),
      customFieldTypes,
    })
  }

  let rawValue = item.value

  if (item.field === 'costInCents') {
    rawValue = normalizeCostInCentsValue(rawValue)
  }

  const normalizedValue = normalizeSingleValueForPrimitiveType(
    rawValue,
    primitiveType
  )

  if (normalizedValue == null) {
    return null
  }

  return buildBaseFilter({
    field: item.field,
    operator,
    value: [normalizedValue],
    customFieldTypes,
  })
}

export function buildBackendFilter(params: {
  filterModel: GridFilterModel
  timezone: string,
  customFieldTypes?: LeadCustomFieldTypes
}): BackendFilter | null {
  const { filterModel, timezone, customFieldTypes } = params

  const gridFilters = filterModel.items
    .map((item) => gridFilterItemToBackendFilter(item, timezone, customFieldTypes))
    .filter(Boolean) as BackendBaseFilter[]

  const gridFilterGroup =
    gridFilters.length === 0
      ? null
      : gridFilters.length === 1
        ? gridFilters[0]
        : {
            [filterModel.logicOperator === 'or' ? 'or' : 'and']: gridFilters,
          }

  const allTopLevelFilters = [
    ...(gridFilterGroup ? [gridFilterGroup] : []),
  ]

  if (allTopLevelFilters.length === 0) {
    return null
  }

  if (allTopLevelFilters.length === 1) {
    return allTopLevelFilters[0]
  }

  return {
    and: allTopLevelFilters,
  }
}

export function buildBackendSort(
  sortModel: GridSortModel,
  customFieldTypes: LeadCustomFieldTypes = {},
): BackendSort {
  return sortModel
    .filter((sort) => Boolean(sort.field) && Boolean(sort.sort))
    .filter((sort) => sort.sort === 'asc' || sort.sort === 'desc')
    .filter((sort) => sort.field !== 'rowMenu')
    .reduce<BackendSort>((acc, sort) => {
      const columnConfig = getBackendColumnConfig(sort.field, customFieldTypes)
      const columnName = isCustomAttributeField(sort.field)
        ? `client-campaigns.custom-attributes:${getCustomAttributeName(sort.field)}`
        : columnConfig.sortName || columnConfig.columnName

      acc[columnName] = sort.sort as BackendSortDirection
      return acc
    }, {})
}

export function buildLeadsRequestBody(params: {
  paginationModel?: GridPaginationModel
  sortModel?: GridSortModel
  filterModel: GridFilterModel
  timezone: string
  customFieldTypes?: LeadCustomFieldTypes
}) {
  const {
    paginationModel,
    sortModel,
    filterModel,
    timezone,
    customFieldTypes,
  } = params

  return {
    ...(paginationModel && {
      page: paginationModel.page + 1,
      per_page: paginationModel.pageSize,
    }),
    timezone,
    filter: buildBackendFilter({ filterModel, timezone, customFieldTypes }),
    ...(sortModel && { order: buildBackendSort(sortModel, customFieldTypes) }),
  }
}

export function buildCustomFieldTypes(
  leadCustomFields: Array<{ name: string; fieldType: string }>
): LeadCustomFieldTypes {
  return Object.fromEntries(
    leadCustomFields.map((field) => [field.name, field.fieldType])
  )
}
